// Клиент API модуля «Видеопоток» (тёмный / пользовательский).
//
// USE_MOCK = true  → локальный мок (демо, ниже по файлу).
// USE_MOCK = false → реальный бэкенд video-stream-dashboard-controller:
//   GET /api/video-streams/meta      → списки фильтров + границы дат
//   GET /api/video-streams/dashboard → { summary, breakdowns, items[] }
//   GET /api/video-streams/{id}/file → сам видеофайл (Range) — идёт в video.url
//
// Файл {id}/file отдаётся публично (permitAll) и поддерживает Range, поэтому
// <video src={url}> играет потоково и с перемоткой — blob-обёртки не нужны.
//
// Адаптеры normalizeMeta/normalizeDashboard приводят реальный ответ бэка
// (value/value_kk, плоские *_kk в items[]) к контракту компонентов ({ru,kk}).

import { BaseAddress } from "../../config"; // ← проверь путь к своему config
import performRequest from "../../utils/requestHelpers"; // ← и путь к помощнику запросов

const USE_MOCK = false; // ← реальный бэкенд
const BASE = BaseAddress.Api; // ← сервис видеопотоков

/* ----------------------------- Вспомогательное ---------------------------- */

const withAuth = (option) => {
  const token = localStorage.getItem("token");
  return token
    ? {
        ...option,
        headers: {
          ...(option && option.headers),
          Authorization: "Bearer " + token,
        },
      }
    : option;
};

// Если performRequest вернёт сырой Response — распарсим; если уже объект — вернём как есть.
const toJson = (r) => (r && typeof r.json === "function" ? r.json() : r);

// Относительный url файла → абсолютный (нативный <video> требует полный адрес).
const abs = (url) => (!url ? null : url.startsWith("http") ? url : BASE + url);

/* ------------------- Адаптеры: формат бэка → контракт фронта -------------- */

const pair = (ru, kk) => ({ ru: ru ?? "", kk: kk ?? ru ?? "" });

const normalizeMeta = (raw) => {
  const d = raw?.data ?? raw ?? {};
  const opt = (a = []) =>
    a.map((o) => ({ ru: o.value, kk: o.value_kk ?? o.value }));
  return {
    sources: opt(d.sources),
    addresses: opt(d.addresses),
    locations: opt(d.locations),
    date_bounds: d.date_bounds ?? { plan: {}, fact: {} },
  };
};

const normBreakdown = (a = []) =>
  a.map((b) => ({
    value_ru: b.value,
    value_kk: b.value_kk ?? b.value,
    count: b.count,
    share_pct: b.share_pct,
    is_unspecified: b.is_unspecified,
  }));

const normalizeDashboard = (raw) => {
  const d = raw?.data ?? raw ?? {};
  return {
    summary: d.summary ?? {
      total: 0,
      sources_count: 0,
      addresses_count: 0,
      locations_count: 0,
      with_file: 0,
      without_file: 0,
    },
    breakdowns: {
      by_source: normBreakdown(d.breakdowns?.by_source),
      by_address: normBreakdown(d.breakdowns?.by_address),
      by_location: normBreakdown(d.breakdowns?.by_location),
    },
    items: (d.items ?? []).map((it) => ({
      id: it.id,
      source_name: pair(it.source_name, it.source_name_kk),
      address: pair(it.address, it.address_kk),
      location: pair(it.location, it.location_kk),
      plan_date: it.plan_date ?? null,
      fact_date: it.fact_date ?? null,
      video: {
        available: !!it.video?.available,
        url: abs(it.video?.url),
        file_name: it.video?.file_name ?? null,
        mime_type: it.video?.mime_type ?? null,
        duration_sec: it.video?.duration_sec ?? null,
      },
    })),
  };
};

/* ------------------------------ Реальные вызовы --------------------------- */

async function realMeta() {
  const raw = await performRequest(
    `${BASE}/api/video-streams/meta`,
    withAuth(),
  );
  return normalizeMeta(await toJson(raw));
}

async function realDashboard(params = {}) {
  const qs = new URLSearchParams();
  qs.set("date_kind", params.date_kind || "plan");
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  if (params.source_name) qs.set("source_name", params.source_name);
  if (params.address) qs.set("address", params.address);
  if (params.location) qs.set("location", params.location);
  const raw = await performRequest(
    `${BASE}/api/video-streams/dashboard?${qs.toString()}`,
    withAuth(),
  );
  return normalizeDashboard(await toJson(raw));
}

/* =========================================================================
   МОК (демо). Включается флагом USE_MOCK. Форма — уже контрактная {ru,kk}.
   ========================================================================= */

const SAMPLE =
  "https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/360/Big_Buck_Bunny_360_10s_1MB.mp4";

const withFile = (id) => ({
  available: true,
  url: SAMPLE,
  file_name: `stream-${String(id).padStart(2, "0")}.mp4`,
  mime_type: "video/mp4",
  duration_sec: 21,
});
const noFile = {
  available: false,
  url: null,
  file_name: null,
  mime_type: null,
  duration_sec: null,
};

const STREAMS = [
  {
    id: 1,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "ул. Сатпаева, 12", kk: "Сәтпаев к-сі, 12" },
    location: { ru: "Центральная площадь", kk: "Орталық алаң" },
    plan_date: "2025-03-31",
    fact_date: "2025-03-24",
    video: withFile(1),
  },
  {
    id: 2,
    source_name: { ru: "Купольная камера PTZ", kk: "PTZ күмбез камерасы" },
    address: { ru: "ул. Сатпаева, 12", kk: "Сәтпаев к-сі, 12" },
    location: { ru: "Центральная площадь", kk: "Орталық алаң" },
    plan_date: "2025-03-31",
    fact_date: "2025-04-18",
    video: withFile(2),
  },
  {
    id: 3,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "ул. Абая, 45", kk: "Абай к-сі, 45" },
    location: { ru: "Здание акимата района", kk: "Аудан әкімдігінің ғимараты" },
    plan_date: "2025-06-30",
    fact_date: "2025-06-12",
    video: withFile(3),
  },
  {
    id: 4,
    source_name: { ru: "Камера распознавания ГРНЗ", kk: "МТН тану камерасы" },
    address: {
      ru: "трасса Атырау–Кульсары, 12 км",
      kk: "Атырау–Құлсары тас жолы, 12 км",
    },
    location: { ru: "Южный въезд в город", kk: "Қалаға оңтүстік кіреберіс" },
    plan_date: "2025-06-30",
    fact_date: "2025-07-15",
    video: withFile(4),
  },
  {
    id: 5,
    source_name: {
      ru: "Камера фиксации нарушений ПДД",
      kk: "ЖҚЕ бұзушылықтарын тіркеу камерасы",
    },
    address: {
      ru: "трасса Атырау–Кульсары, 12 км",
      kk: "Атырау–Құлсары тас жолы, 12 км",
    },
    location: { ru: "Южный въезд в город", kk: "Қалаға оңтүстік кіреберіс" },
    plan_date: "2025-09-30",
    fact_date: "2025-09-26",
    video: withFile(5),
  },
  {
    id: 6,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "мкр. Береке, 3", kk: "Береке ы/а, 3" },
    location: { ru: "Рынок «Береке»", kk: "«Береке» базары" },
    plan_date: "2025-09-30",
    fact_date: "2025-10-08",
    video: withFile(6),
  },
  {
    id: 7,
    source_name: { ru: "Купольная камера PTZ", kk: "PTZ күмбез камерасы" },
    address: { ru: "мкр. Береке, 3", kk: "Береке ы/а, 3" },
    location: { ru: "Рынок «Береке»", kk: "«Береке» базары" },
    plan_date: "2025-12-31",
    fact_date: null,
    video: noFile,
  },
  {
    id: 8,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "пр. Достык, 8", kk: "Достық д-лы, 8" },
    location: { ru: "Городской парк", kk: "Қалалық саябақ" },
    plan_date: "2025-12-31",
    fact_date: "2025-12-19",
    video: withFile(8),
  },
  {
    id: 9,
    source_name: {
      ru: "Камера фиксации нарушений ПДД",
      kk: "ЖҚЕ бұзушылықтарын тіркеу камерасы",
    },
    address: { ru: "пр. Достык, 8", kk: "Достық д-лы, 8" },
    location: { ru: "Городской парк", kk: "Қалалық саябақ" },
    plan_date: null,
    fact_date: "2026-01-23",
    video: withFile(9),
  },
  {
    id: 10,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "ул. Жамбыла, 21", kk: "Жамбыл к-сі, 21" },
    location: { ru: "Автовокзал", kk: "Автовокзал" },
    plan_date: "2026-03-31",
    fact_date: "2026-03-11",
    video: withFile(10),
  },
  {
    id: 11,
    source_name: { ru: "Камера распознавания ГРНЗ", kk: "МТН тану камерасы" },
    address: { ru: "ул. Жамбыла, 21", kk: "Жамбыл к-сі, 21" },
    location: { ru: "Автовокзал", kk: "Автовокзал" },
    plan_date: "2026-03-31",
    fact_date: null,
    video: withFile(11),
  },
  {
    id: 12,
    source_name: { ru: "Купольная камера PTZ", kk: "PTZ күмбез камерасы" },
    address: { ru: "ул. Абая, 45", kk: "Абай к-сі, 45" },
    location: { ru: "Стадион", kk: "Стадион" },
    plan_date: "2026-06-30",
    fact_date: "2026-06-05",
    video: withFile(12),
  },
  {
    id: 13,
    source_name: { ru: "Обзорная камера", kk: "Шолу камерасы" },
    address: { ru: "ул. Абая, 45", kk: "Абай к-сі, 45" },
    location: { ru: "Школа №1", kk: "№1 мектеп" },
    plan_date: "2026-06-30",
    fact_date: null,
    video: withFile(13),
  },
  {
    id: 14,
    source_name: {
      ru: "Камера фиксации нарушений ПДД",
      kk: "ЖҚЕ бұзушылықтарын тіркеу камерасы",
    },
    address: { ru: "ул. Сатпаева, 12", kk: "Сәтпаев к-сі, 12" },
    location: { ru: "Школа №1", kk: "№1 мектеп" },
    plan_date: "2026-09-30",
    fact_date: null,
    video: noFile,
  },
];

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

function uniqueField(field) {
  const seen = new Map();
  for (const s of STREAMS)
    if (!seen.has(s[field].ru)) seen.set(s[field].ru, s[field]);
  return [...seen.values()].sort((a, b) => a.ru.localeCompare(b.ru, "ru"));
}

async function mockMeta() {
  await delay(0);
  const bound = (kind) => {
    const ds = STREAMS.map((s) => (kind === "plan" ? s.plan_date : s.fact_date))
      .filter(Boolean)
      .sort();
    return ds.length
      ? { min: ds[0], max: ds[ds.length - 1] }
      : { min: null, max: null };
  };
  return {
    sources: uniqueField("source_name"),
    addresses: uniqueField("address"),
    locations: uniqueField("location"),
    date_bounds: { plan: bound("plan"), fact: bound("fact") },
  };
}

function breakdown(rows, field) {
  const m = new Map();
  for (const s of rows) {
    const k = s[field].ru;
    if (!m.has(k))
      m.set(k, { value_ru: s[field].ru, value_kk: s[field].kk, count: 0 });
    m.get(k).count += 1;
  }
  const total = rows.length;
  return [...m.values()]
    .sort(
      (a, b) => b.count - a.count || a.value_ru.localeCompare(b.value_ru, "ru"),
    )
    .map((e) => ({ ...e, share_pct: total ? (e.count / total) * 100 : 0 }));
}

async function mockDashboard(params = {}) {
  await delay(0);
  const {
    date_kind = "plan",
    date_from = "",
    date_to = "",
    source_name = "",
    address = "",
    location = "",
  } = params;

  const rows = STREAMS.filter((s) => {
    const d = date_kind === "plan" ? s.plan_date : s.fact_date;
    if (date_from || date_to) {
      if (!d) return false;
      if (date_from && d < date_from) return false;
      if (date_to && d > date_to) return false;
    }
    if (source_name && s.source_name.ru !== source_name) return false;
    if (address && s.address.ru !== address) return false;
    if (location && s.location.ru !== location) return false;
    return true;
  });

  const items = rows
    .slice()
    .sort(
      (a, b) =>
        a.source_name.ru.localeCompare(b.source_name.ru, "ru") ||
        a.address.ru.localeCompare(b.address.ru, "ru") ||
        a.id - b.id,
    );

  const uniq = (field) => new Set(items.map((s) => s[field].ru)).size;
  const withFileCount = items.filter((s) => s.video.available).length;

  const summary = {
    total: items.length,
    sources_count: uniq("source_name"),
    addresses_count: uniq("address"),
    locations_count: uniq("location"),
    with_file: withFileCount,
    without_file: items.length - withFileCount,
  };

  return {
    summary,
    breakdowns: {
      by_source: breakdown(items, "source_name"),
      by_address: breakdown(items, "address"),
      by_location: breakdown(items, "location"),
    },
    items,
  };
}

/* -------------------------------- Точки входа ----------------------------- */

export const getMeta = () => (USE_MOCK ? mockMeta() : realMeta());
export const getDashboard = (params) =>
  USE_MOCK ? mockDashboard(params) : realDashboard(params);
